import type { OBJECTS, Patch } from "../../server/servicem8/objects";
import type { Stored } from "../../server/servicem8/store";
import type { TaxRate } from "../../shared/money";
import type { DataProgress } from "../../shared/sync";
import { presetPeriod } from "../../shared/periods";
import { plural } from "../../shared/words";
import type { NameChange, NameTagConfig } from "./names";
import { type ToolMeta, type ToolPermission, USE_PERMISSION } from "../../shared/tools";

/**
 * What a security role can do in the Price List (Settings › Access), in the order shown. Each is
 * checked on the server for the change it covers (server.ts), and screens follow it (data.can).
 */
export const PRICELIST_PERMISSIONS: readonly ToolPermission[] = [
    { id: USE_PERMISSION, group: "High level", label: "Use the Price List", description: "Open it, and see its items, suppliers, groups and bundles." },
    {
        id: "items",
        group: "What they can change",
        label: "Manage items",
        description: "Add, edit, archive and restore items and bundles; sync them with ServiceM8; review unknown and changed records.",
    },
    {
        id: "pricing",
        group: "What they can change",
        label: "Manage pricing",
        description: "Prices and pricing rules, how items are taxed, costs and where they come from, and groups. Needs See costs.",
        requires: "costs",
    },
    {
        id: "suppliers",
        group: "What they can change",
        label: "Manage suppliers",
        description: "Suppliers and their details, and their prices: adding, updating and rechecking them. Needs See costs.",
        requires: "costs",
    },
    {
        id: "costs",
        group: "What they can see",
        label: "See costs",
        description: "Item costs, supplier prices and their history, pricing rules and groups (a markup gives the cost away), and markups and profit.",
        // Off for a role that could only look: what the business pays and makes isn't for everyone.
        viewOnly: "disabled",
        // Added after the others: roles already managing pricing or suppliers keep seeing what they did.
        startsOnWith: ["pricing", "suppliers"],
    },
    {
        id: "stats",
        group: "What they can see",
        label: "See stats",
        description: "Stats tabs on items, suppliers and groups (use on jobs, sales and profit), and items not used lately. Needs See costs.",
        // Profit is worked out from costs. Off for a role that can only look, as See costs is.
        requires: "costs",
    },
    { id: "settings", group: "What they can change", label: "Change settings", description: "Tax on purchases, supplier price checks, minimum markup, tags, name tags and job time." },
];

export const meta: ToolMeta = {
    id: "pricelist",
    name: "Price List",
    description: "Materials and pricing, synced both ways with ServiceM8.",
    icon: "Tags",
    scopes: ["read_inventory"],
    datasets: ["material", "materialbundle", "taxrate", "supplier", "vendor"],
    permissions: PRICELIST_PERMISSIONS,
    // What's sold (ServiceM8's items and bundles), then the Catalogue: what it's made of, and what that costs (catalogue.ts).
    sections: [
        { id: "materials", name: "Items", icon: "Package" },
        { id: "bundles", name: "Bundles", icon: "Boxes" },
        { id: "products", name: "Products", icon: "Puzzle", heading: { name: "Catalogue", icon: "Library", settingsAt: "tax-on-purchases" } },
        { id: "groups", name: "Groups", icon: "Layers" },
        { id: "suppliers", name: "Suppliers", icon: "Truck" },
    ],
    // One settings page for both, Settings › Price List (SettingsSection.tsx).
    settings: true,
};


/** What the Price List's routes and screens check (what's being done), and the permission each needs (at least an option of it; default its most). */
export const PRICELIST_ACTIONS = {
    // Items and bundles: Manage items.
    "items.add": ["items"],
    "items.edit": ["items"],
    "items.archive": ["items"],
    "items.review": ["items"],
    bundles: ["items"],
    // Prices, costs and groups: Manage pricing.
    "items.pricing": ["pricing"],
    "costs.edit": ["pricing"],
    groups: ["pricing"],
    // Suppliers and their prices: Manage suppliers.
    "prices.add": ["suppliers"],
    "prices.update": ["suppliers"],
    "suppliers.edit": ["suppliers"],
    settings: ["settings"],
    // Looking: See costs, See stats.
    "costs.view": ["costs"],
    stats: ["stats"],
} as const satisfies Record<string, readonly [string] | readonly [string, string]>;
export type PriceListAction = keyof typeof PRICELIST_ACTIONS;

/** Actions that only look: they don't need ServiceM8 connected. */
export const LOOKING_ACTIONS: ReadonlySet<PriceListAction> = new Set(["costs.view", "stats"]);

/** Marking a record's changes from ServiceM8 as seen: for whoever manages that kind of record. */
export const REVIEW_ACTIONS = { material: "items.review", supplier: "suppliers.edit", materialbundle: "bundles" } as const satisfies Record<TrackedObject, PriceListAction>;

/** The address keys that open the Price List's windows, over any page (windows.ts). */
export const WINDOW_KINDS = ["item", "product", "supplier", "bundle", "group"] as const;
export type WindowKind = (typeof WINDOW_KINDS)[number];
/** Every address key its windows use: kept as you move between pages, so they stay open. */
export const WINDOW_PARAMS = [...WINDOW_KINDS, "itemTab", "productTab", "supplierTab", "bundleTab", "groupTab", "rename"] as const;

/*
 * Types shared by the server and the screens. ServiceM8 records (materials, bundles, suppliers)
 * are typed from their descriptions in servicem8/objects.ts.
 */

/**
 * A material, as described in servicem8/objects.ts (ServiceM8's own field names, so an edit goes
 * to ServiceM8 as-is). Price and cost are decimal strings in the item's basis (`price_includes_taxes`
 * covers both). `hidden`: archived in ServiceM8 but kept in use here, on standby (states.ts).
 */
export type MaterialRow = Stored<typeof OBJECTS.material> & { hidden: boolean };

/** For sale (active in ServiceM8), hidden (archived there, on standby: kept priced here) or retired (archived, done with): states.ts. */
export const ITEM_STATES = ["sale", "hidden", "retired"] as const;
export type ItemState = (typeof ITEM_STATES)[number];

export function itemState(m: Pick<MaterialRow, "active" | "hidden">): ItemState {
    if (m.active) return "sale";
    return m.hidden ? "hidden" : "retired";
}

/** In use: for sale, or hidden (on standby, ready to unhide). Its price, cost and item number matter. */
export const inUse = (m: Pick<MaterialRow, "active" | "hidden">) => m.active || m.hidden;

/**
 * A product in the Catalogue (catalogue.ts): kept only in this app, what items sold in ServiceM8 are built from. Every other
 * material is an item: in ServiceM8.
 */
export const isProduct = (m: Pick<MaterialRow, "synced">) => !m.synced;

/** Editable ServiceM8 fields of a material. */
export type MaterialPatch = Patch<typeof OBJECTS.material>;

/** A change to a material: its fields, and/or one change to its name's parts (applied to the latest name; see names.ts). */
export type MaterialUpdate = MaterialPatch & { nameChange?: NameChange };

/** manual = Manual (a price, kept; a fixed-price group's too); percent = Markup (a % on the cost: typed, or a markup group's). */
export const PRICE_MODES = ["manual", "percent"] as const;
export type PriceMode = (typeof PRICE_MODES)[number];

/**
 * How an item's price is worked out (app-owned: only the resulting price goes to ServiceM8): cost + markup, or a fixed price.
 * Amounts are stored before tax (see shared/money.ts), so they mean the same whatever the item's basis; screens show and take them
 * in the item's basis.
 */
export interface PricingRule {
    mode: PriceMode;
    /** percent (Markup): e.g. "35". */
    markupValue: string;
    /** manual (Manual, or a fixed-price group's): the price, before tax. */
    basePrice: string;
    /** percent, from a markup group: its tiers, giving the markup by cost (pricing.ts markupOf) in place of `markupValue`. */
    tiers?: MarkupTier[] | null;
}

export interface ItemPricing extends PricingRule {
    /** The price this app last saved. If ServiceM8's price differs, someone edited it there. */
    appliedPrice: string | null;
    /** The group it's in, if any: the rule above is then the group's (unless it overrides it)... */
    groupId: string | null;
    /** ...and (a fixed price) so is how it's taxed. */
    groupTax: GroupTaxBasis | null;
    /**
     * In its group, but priced its own way (an exception): the rule above is its own, and it's taxed as it is (no groupTax). Still in
     * the group for its tags, lists and stats.
     */
    overridesGroup?: boolean;
    /** ServiceM8 prices it itself (its own pricing strategy: servicem8Pricing.ts): this pricing is shown, not used. */
    pricedInServiceM8?: boolean;
    /** Labour time (labourTime.ts): its rule above is then the fixed price that share of the rate gives, and its cost follows the rate's. */
    labour?: LabourTime;
    /** Built from products (packages.ts): its rule above is then the fixed price they add up to, and its cost should be theirs (else to update). */
    fromParts?: boolean;
    /** Built from products, one of which is gone: its price is the one last worked out from them, and its cost isn't followed. */
    partMissing?: boolean;
}

/**
 * A set time on an hourly labour product (its rate): a product's price and cost are that share of the rate's (20 min of $180/hr
 * is $60), so changing the rate shows them all as prices and costs to update. E.g. "Install standard lockset": 30 min of Labour
 * Per Hour, linked into every item installed that way.
 */
export interface LabourTime {
    rateUuid: string;
    minutes: number;
    /** The rate is gone: its price is the one last worked out from it, and its cost isn't followed. */
    rateMissing?: boolean;
}

/** A labour time's longest (100 hours). */
export const MAX_LABOUR_MINUTES = 6000;
/** A labour time's shortest. */
export const MIN_LABOUR_MINUTES = 1;
export const LABOUR_MINUTES_RANGE = `A time from ${MIN_LABOUR_MINUTES} minute to ${MAX_LABOUR_MINUTES / 60} hours`;

/** Its group prices it (in one, not overriding it): the group's changes and updates reach it, and its own rule isn't edited. */
export const usesGroup = (p: Pick<ItemPricing, "groupId" | "overridesGroup"> | undefined) => Boolean(p?.groupId && !p.overridesGroup);

/**
 * How an amount is taxed: a ServiceM8 tax rate ("" = the account's default), and whether the amount
 * includes it. How a supplier quotes a price ("$22 inc GST"), how a cost was typed, how a fixed-price
 * group taxes its items. Amounts are stored before tax either way: this is how they're shown and typed.
 */
export interface TaxQuote {
    rateUuid: string;
    includesTax: boolean;
}

/** How a fixed-price group taxes its items (its rate: the account's default when it has none, or it's gone). */
export type GroupTax = TaxQuote;

/** A group's tax, with its rate as a fraction (0.1 for 10%), for working out prices. */
export interface GroupTaxBasis extends GroupTax {
    tax: number;
}

/** Price List settings for a business. */
export interface PriceListSettings {
    /**
     * Short names for tax rates (rate uuid -> name), for a long ServiceM8 name ("GST on Income") read
     * shortly ("GST") wherever the Price List shows it (read.ts taxRates). Only here: never sent to
     * ServiceM8, whose names (and the accounting software's link to them) stay as they are.
     */
    taxShortNames: Record<string, string>;
    /**
     * Tax paid on purchases is claimed back (a tax-registered business), so it isn't part of what
     * things cost. Off: it's a real cost. Used for supplier prices.
     */
    purchaseTaxClaimed: boolean;
    /** Items with a markup on their cost under this % show as making too little (0 = only a loss). */
    minMarkup: number;
    /** Supplier prices not checked for this many days need rechecking (amber), and after `outdatedDays` they're out of date (red). */
    recheckDays: number;
    outdatedDays: number;
    /** Which jobs, and how, work out how long items take (jobTime.ts). */
    jobTime: JobTimeSettings;
}

/** A tier of a markup group: its markup (%, e.g. "50") on costs from its minimum (before tax) up to the next tier's. */
export interface MarkupTier {
    minCost: string;
    markup: string;
}

/**
 * How items' time on jobs is worked out (jobTime.ts): each counted job's time is shared out between the items on it. Only
 * completed jobs count (a work order's time and items may not all be in yet). Each
 * business sets its own: how it records time, and which jobs or items don't follow its usual work.
 */
export interface JobTimeSettings {
    /** Jobs dated (completed, else made a work order) within this many months. */
    lookBackMonths: number;
    /** A job's time includes ServiceM8's estimated drive to it (checking in can start from navigating there). Off: it's taken off. */
    countDrive: boolean;
    /** Jobs taking less than this, or more, are mistakes (a tap in and out, a forgotten check-out): left out. Minutes. */
    minMinutes: number;
    maxMinutes: number;
    /** Customers whose jobs are left out (their work isn't like the rest), with their names as when picked. */
    leaveOutCustomers: { uuid: string; name: string }[];
    /** Items not timed (their quantity isn't work, e.g. a discount): their jobs still count for the other items on them. */
    leaveOutItems: string[];
    /** Jobs with an archived item on are left out (and so archived items aren't timed). */
    leaveOutArchived: boolean;
    /**
     * Counted jobs an item needs to be on before it has a time of its own (fewer: it shares one with the other rare items), and
     * jobs with more than one of it before each extra one does.
     */
    minJobs: number;
    /**
     * Several people on a job: its time counted once (the time anyone was there: people there together count once, one after the
     * other both count), or each person's added up (person-time).
     */
    severalPeople: "once" | "added";
}

export const JOB_TIME_DEFAULTS: JobTimeSettings = {
    lookBackMonths: 24,
    countDrive: true,
    minMinutes: 0,
    maxMinutes: 12 * 60,
    leaveOutCustomers: [],
    leaveOutItems: [],
    leaveOutArchived: true,
    minJobs: 10,
    severalPeople: "once",
};

export interface PriceListData {
    /** For each action (PRICELIST_ACTIONS): null when this person can, else why not (their role, or ServiceM8 not connected). */
    whyNot: Record<PriceListAction, string | null>;
    materials: MaterialRow[];
    taxRates: TaxRate[];
    settings: PriceListSettings;
    /** The business's name tags (names.ts). */
    nameTags: NameTagConfig;
    /** Material uuid -> how its price is worked out. Not listed = a plain price, typed in. */
    itemPricing: Record<string, ItemPricing>;
    suppliers: SupplierRow[];
    supplierPrices: SupplierPriceRow[];
    /** Materials whose cost follows a supplier price. */
    costFollows: string[];
    /** Of those, the ones following a price picked for them (material uuid -> price id); the rest follow the cheapest. */
    costPrices: Record<string, string>;
    /** Active items whose cost isn't what the supplier price it follows gives: material uuid -> that cost. "Cost to update" until someone updates it. */
    costUpdates: Record<string, string>;
    /** Costs are being updated now ("Update all": in the background). */
    costsUpdating: boolean;
    /** How an item's typed cost was last typed (material uuid -> quote); others start ex its own rate. */
    costQuotes: Record<string, TaxQuote>;
    /** An item's typed cost exactly as last typed (in its costQuotes quote), shown back while ServiceM8's cost still matches it. */
    costTyped: Record<string, string>;
    /** Why suppliers can't be added or changed right now (suppliers.ts: ServiceM8's Suppliers add-on), or null. */
    suppliersUnavailable: string | null;
    /** Pricing groups, by name (groups.ts). */
    groups: GroupRow[];
    bundles: BundleRow[];
    /** Why bundles can't be added or changed right now (bundles.ts), or null. */
    bundlesUnavailable: string | null;
    /** Items, suppliers and bundles changed in ServiceM8 that no one has seen yet (history.ts): each a warning until seen. */
    changedInServiceM8: ChangedRecords;
    /** Active items no one has said are real yet (known.ts): each a warning until marked known. */
    unknownItems: string[];
    /** Items merged into others as duplicates (merge.ts): merged uuid -> the one kept. Left out of the Archived list. */
    mergedItems: Record<string, string>;
    /** Items, products, bundles, suppliers and groups checked, and still as they were then (checked.ts): by uuid (a group's id). */
    checked: Record<string, CheckedMark>;
    /** The business's tags, in order (itemTags.ts): labels, some putting wording in names (smartTags.ts). */
    tags: TagRow[];
    /** ...and the sets they can be in. */
    tagSets: TagSetRow[];
    /** Item or bundle uuid -> its tags (ids). Not listed: none. */
    itemTags: Record<string, string[]>;
    /** Items ServiceM8 prices itself, with one of its pricing strategies (servicem8Pricing.ts): their pricing here isn't used. */
    pricedInServiceM8: string[];
    /** When each item was last on a job (stats.ts: "YYYY-MM-DD"; not there: never), or null until jobs and job lines are read in full. */
    lastUsed: Record<string, string> | null;
    /** Packages (packages.ts): item uuid -> its parts, in order. Not listed: an ordinary item. */
    packages: Record<string, PackagePart[]>;
}

/** Records some change was to, by kind (rows.ts, changed.ts): items, suppliers, bundles. */
export type RecordScope = Partial<Record<"material" | "supplier" | "materialbundle", string[]>>;

/**
 * The Price List's data for some records only (rows.ts): what replaces theirs in PriceListData.
 * Each kind lists the uuids asked for (`uuids`), and the rows of those still here (the rest are gone).
 */
/** What can be checked (checked.ts): items and products, bundles, suppliers and groups. */
export const CHECKED_KINDS = ["material", "materialbundle", "supplier", "group"] as const;
export type CheckedKind = (typeof CHECKED_KINDS)[number];

/** Who may check (or uncheck) each: whoever may change it. */
export const CHECKED_BY: Record<CheckedKind, PriceListAction> = { material: "items.edit", materialbundle: "bundles", supplier: "suppliers.edit", group: "groups" };

/** Checked (checked.ts): when (ms), and by whom (their name; null if they've gone). */
export interface CheckedMark {
    at: number;
    by: string | null;
}

/** What can have photos (photos.ts): products, and groups. */
export const PHOTO_KINDS = ["material", "group"] as const;
export type PhotoKind = (typeof PHOTO_KINDS)[number];

/** Who may add, caption, move and delete each's photos: whoever may change it. Anyone who can see it sees them. */
export const PHOTOS_BY: Record<PhotoKind, PriceListAction> = { material: "items.edit", group: "groups" };

/** At most this many photos each. */
export const PHOTOS_MAX = 30;
export const PHOTO_CAPTION_MAX = 200;
/** As uploaded (shrunk by the browser first: PhotosTab.tsx): the photo, and its small copy for the grid. */
export const PHOTO_MAX_BYTES = 3_000_000;
export const PHOTO_SMALL_MAX_BYTES = 300_000;

/** A photo (photos.ts), in order. Its files: /photos/:id (`?small` for the grid's copy). */
export interface PhotoRow {
    id: string;
    caption: string;
    width: number;
    height: number;
    /** When (ms), and by whom (their name; null if they've gone). */
    at: number;
    by: string | null;
}

export interface PriceListRows {
    material?: {
        uuids: string[];
        rows: MaterialRow[];
        checked: Record<string, CheckedMark>;
        itemPricing: Record<string, ItemPricing>;
        supplierPrices: SupplierPriceRow[];
        costFollows: string[];
        costPrices: Record<string, string>;
        costUpdates: Record<string, string>;
        costQuotes: Record<string, TaxQuote>;
        costTyped: Record<string, string>;
        unknownItems: string[];
        changed: string[];
        pricedInServiceM8: string[];
        itemTags: Record<string, string[]>;
        /** Null: not allowed, or jobs not read in full yet (as PriceListData's). */
        lastUsed: Record<string, string> | null;
        packages: Record<string, PackagePart[]>;
    };
    supplier?: { uuids: string[]; rows: SupplierRow[]; changed: string[]; checked: Record<string, CheckedMark> };
    materialbundle?: { uuids: string[]; rows: BundleRow[]; changed: string[]; itemTags: Record<string, string[]>; checked: Record<string, CheckedMark> };
}

/** ServiceM8: item numbers are at most 30 characters, unique across materials and bundles. */
export const ITEM_NUMBER_MAX = 30;
/** A money amount as typed: digits, optionally negative, up to 4 decimals. */
export const MONEY_PATTERN = /^-?\d+(\.\d{1,4})?$/;
/** A markup as stored: up to 8 decimals (a % switched from an amount is kept exactly: pricing.ts percentFor). */
export const MARKUP_PATTERN = /^\d+(\.\d{1,8})?$/;

// ---------------------------------------------------------------------------------------------
// Suppliers
// ---------------------------------------------------------------------------------------------

/** Ways a supplier can be paid. */
export const PAYMENT_METHODS = [
    { id: "account", label: "On account" },
    { id: "card", label: "Card" },
    { id: "transfer", label: "Bank transfer" },
    { id: "cash", label: "Cash" },
] as const;

/** Ways to order from a supplier, each with a detail to fill in (where to go, or send it). */
export const ORDER_METHODS = [
    { id: "online", label: "Online store", detail: "Website", placeholder: "https://", kind: "url" },
    { id: "po", label: "Purchase order", detail: "Email it to", placeholder: "orders@supplier.com", kind: "email" },
    { id: "quote", label: "Email for a quote", detail: "Email", placeholder: "sales@supplier.com", kind: "email" },
    { id: "phone", label: "Phone", detail: "Number", placeholder: "Phone number", kind: "phone" },
    { id: "counter", label: "In store / trade counter", detail: "Where", placeholder: "Branch or address", kind: "text" },
    { id: "rep", label: "Through our rep", detail: null, placeholder: "", kind: "text" },
] as const;

/** The app's own details for a supplier (ServiceM8 has nowhere for them). */
export interface SupplierDetails {
    /** How their prices are usually quoted (each price starts this way): with tax or without, at which rate ("" = the default rate). */
    pricesIncludeTax: boolean;
    purchaseTaxRateUuid: string;
    hasAccount: boolean;
    accountTerms: string;
    repName: string;
    repPhone: string;
    repEmail: string;
    /** PAYMENT_METHODS ids they take, and the one we prefer (one of them, or null). */
    paymentMethods: string[];
    preferredPayment: string | null;
    /** ORDER_METHODS ids they take, with their detail, and the one we prefer. */
    orderMethods: { method: string; detail: string }[];
    preferredOrder: string | null;
    notes: string;
}

export const NO_DETAILS: SupplierDetails = {
    pricesIncludeTax: false,
    purchaseTaxRateUuid: "",
    hasAccount: false,
    accountTerms: "",
    repName: "",
    repPhone: "",
    repEmail: "",
    paymentMethods: [],
    preferredPayment: null,
    orderMethods: [],
    preferredOrder: null,
    notes: "",
};

/** A supplier: ServiceM8's fields (servicem8/objects.ts), where it lives, and the app's details. */
export type SupplierRow = Stored<typeof OBJECTS.supplier> & { details: SupplierDetails };

/** Editable ServiceM8 fields of a supplier. */
export type SupplierPatch = Patch<typeof OBJECTS.supplier>;

/**
 * Charged by the hour, as ServiceM8's Smart Labour has it: "Labour" or "Labor" in its name (its price is an hour's). Not one whose
 * name gives its own time ("Labour 20 Minutes", "2 hr labour"): that's a set amount of labour, one at a time.
 */
export function isHourlyLabour(name: string) {
    return LABOUR_WORD.test(name) && !/\d+(\.\d+)?\s*(m|mins?|minutes?|h|hrs?|hours?)\b/i.test(name);
}

/** ServiceM8's Smart Labour rule: "Labour" or "Labor" in an item's name makes it labour (an hourly rate, on jobs and in reports). */
export const LABOUR_WORD = /\b(labour|labor)\b/i;

/** Where an item's cost comes from: typed in, its cheapest supplier price, or one price picked for it (bulk or not). */
export type CostSource = { from: "typed" } | { from: "cheapest" } | { from: "price"; priceId: string };

/**
 * The item number a bundle's items suggest (offered when asked, never set by itself): their numbers in its order, joined by dashes
 * ("CYL570-LABCYL"), those at quantity 0 left out; just one, its number then "-KIT" ("CYL570-KIT": a bundle made from an item).
 * Null: nothing in it, one has no number, it'd be too long (typed instead), or it has it already.
 */
export function bundleNumber(materials: readonly Pick<MaterialRow, "uuid" | "item_number">[], bundle: Pick<BundleRow, "material_list" | "item_number">): string | null {
    const numbers: string[] = [];
    for (const l of bundle.material_list) {
        if (l.quantity <= 0) continue;
        const n = materials.find((m) => m.uuid === l.uuid)?.item_number.trim();
        if (!n) return null;
        numbers.push(n);
    }
    const number = numberFromParts(numbers, "-KIT");
    return number && number.toUpperCase() !== bundle.item_number.trim().toUpperCase() ? number : null;
}

/**
 * The item number an item built from products suggests (bundleNumber's rule): their numbers in its order, joined by dashes
 * ("DEADBOLT-LABOUR"), those at quantity 0 left out; just one, its number (the product is what it is). Null: one has no number, it'd
 * be too long, or it's `current` already.
 */
export function builtNumber(materials: readonly Pick<MaterialRow, "uuid" | "item_number">[], parts: readonly PackagePart[], current: string): string | null {
    const numbers: string[] = [];
    for (const p of parts) {
        if (p.quantity <= 0) continue;
        const n = materials.find((m) => m.uuid === p.uuid)?.item_number.trim();
        if (!n) return null;
        numbers.push(n);
    }
    const number = numberFromParts(numbers, "");
    return number && number.toUpperCase() !== current.trim().toUpperCase() ? number : null;
}

/**
 * The item number parts suggest (bundleNumber's rule, a package's too): their numbers in order, joined by dashes, each once; just
 * one, its number then `single` ("-KIT", "-PKG"). Null: none, or too long (typed instead).
 */
export function numberFromParts(numbers: readonly string[], single: string): string | null {
    const unique: string[] = [];
    for (const n of numbers.map((x) => x.trim()).filter(Boolean)) if (!unique.some((x) => x.toUpperCase() === n.toUpperCase())) unique.push(n);
    const number = unique.length === 1 ? `${unique[0]}${single}` : unique.join("-");
    return unique.length && number.length <= ITEM_NUMBER_MAX ? number : null;
}

/** What a supplier charges for one material, from a minimum quantity. Stored before tax. */
export interface SupplierPriceRow {
    id: string;
    materialUuid: string;
    supplierUuid: string;
    supplierCode: string;
    /** Link to the item on the supplier's website ("" = none). */
    url: string;
    /** The least you have to order for this price (1 = no minimum). */
    minQty: number;
    /** Price for one, before tax (decimal string). */
    priceNet: string;
    /** How it's quoted (its rate is the tax paid on it), or null: as its supplier usually quotes (supplierPricing.ts quoteOf). */
    quote: TaxQuote | null;
    /** Epoch ms: last confirmed (changed or not), and last actually changed. */
    checkedAt: number;
    changedAt: number;
}

export interface PriceHistoryEntry {
    id: string;
    priceNet: string;
    minQty: number;
    changedAt: number;
    /** Who made the change (name or email), if known. */
    changedBy: string | null;
    /** Marked as a mistake: left out of the trend. */
    ignored: boolean;
}

/** Re-editing your own price within this window corrects the last history entry instead of adding one. */
export const HISTORY_CORRECTION_MS = 15 * 60_000;

// ---------------------------------------------------------------------------------------------
// Pricing groups, bundles
// ---------------------------------------------------------------------------------------------

/**
 * A pricing group (groups.ts), any kind: a fixed price (`tiers` null: its rule a fixed price, its items taxed as `tax`
 * says), or a markup on each item's cost (`tiers`: one, the same on every cost; more, tiered by cost; its items' tax left alone).
 * Items use one at most; the same window and lists whatever the kind.
 */
export interface GroupRow {
    id: string;
    name: string;
    /** What it's for (its Details tab): only kept here. */
    description: string;
    /** How it prices an item: a fixed price (stored before tax, shown and typed in its tax basis), or Markup by its tiers. */
    rule: PricingRule;
    /** How a fixed price's items are taxed: part of its pricing (an item taxed otherwise doesn't match it). Unused for a markup. */
    tax: GroupTax;
    /** A markup's tiers; null: a fixed price. */
    tiers: MarkupTier[] | null;
}

export type GroupKind = "fixedPrice" | "markup" | "tiered";

/** A group's kind, from its tiers. */
export function groupKind(s: Pick<GroupRow, "tiers">): GroupKind {
    if (!s.tiers) return "fixedPrice";
    return s.tiers.length > 1 ? "tiered" : "markup";
}

/** A kind, in words. */
export const GROUP_KIND_LABEL = { fixedPrice: "Fixed price", markup: "Markup", tiered: "Tiered markup" } as const;

/**
 * What a tag (or a tag set) puts in an item's name (smartTags.ts): in its brackets ("Supply Only"), in its includes or excludes
 * ("Includes Keys": `text` is "Keys"), or a line under it, as it reads ("SPECIAL ORDER ITEM (3-7 BUSINESS DAYS)").
 */
export type Wording = { kind: "brackets" | "includes" | "excludes" | "line"; text: string };

export const WORDING_KINDS = ["brackets", "includes", "excludes", "line"] as const;

/**
 * A tag (itemTags.ts): a label items can have any number of (a category, a keyway). It can put wording in their names (`wording`).
 * A tag with wording is on an item when its name has the wording; one without is kept here.
 * `aliases`: its wording's earlier texts, still read out of names.
 */
export interface TagRow {
    id: string;
    name: string;
    /** The set it's in, or null. */
    setId: string | null;
    wording: Wording | null;
    /** Not in a set: it says what a product is (productTags.ts). In a set, the set's `forProducts` says. */
    forProducts: boolean;
    aliases: string[];
    /** Its place in the order: tags not in a set and sets share one; a set's tags have their own. */
    position: number;
}

/**
 * A set of tags (Installation, Keyway): one only (an item has at most one of them: picking another swaps it), or any number. It
 * can put wording in the name too, when an item has any of its tags (Keyway: "Includes Keys").
 */
export interface TagSetRow {
    id: string;
    name: string;
    oneOnly: boolean;
    wording: Wording | null;
    /**
     * Its tags say what a product is (Keyway, Brand), not how an item's sold (Supply only): they're put on products, and items
     * linked to them have them too (productTags.ts). Groups can give them, or ask each product for one.
     */
    forProducts: boolean;
    aliases: string[];
    position: number;
}

/** Tag names: how long. */
export const TAG_NAME_MAX = 60;

export type { BundleLine } from "../../server/servicem8/objects";

/** One of a package's parts (packages.ts): an item, and how many. */
export interface PackagePart {
    uuid: string;
    quantity: number;
}

/** Parts a package can have (as a bundle's items: a package past this is better split). */
export const PACKAGE_MAX_PARTS = 50;

/** A ServiceM8 material bundle (servicem8/objects.ts): several items sold together. And where it lives. */
export type BundleRow = Stored<typeof OBJECTS.materialbundle>;

/** ServiceM8: a bundle holds at most this many materials. */
export const BUNDLE_MAX_ITEMS = 50;

// ---------------------------------------------------------------------------------------------
// Stats (stats.ts): from the items on jobs
// ---------------------------------------------------------------------------------------------

/**
 * The first day of the month 11 months ago ("2025-11-01"): "the last 12 months" is this month and the 11 before, in the business's
 * days (`zone`: its time zone, as its Stats are).
 */
export const yearStart = (zone?: string | null, now = new Date()) => presetPeriod("last12", now, 1, zone).from;

/**
 * An item's active jobs: quotes, and work orders and completed jobs not yet approved (in ServiceM8 a
 * quote is a job too). The same everywhere it's shown (its Stats tab, its item number's hint).
 */
export const activeJobs = (s: Pick<ItemStats, "openJobs" | "openQuotes">) => s.openJobs + s.openQuotes;

/** "2 active jobs". */
export const activeJobsText = (n: number) => plural(n, "active job");

/** Jobs and job lines, which stats are worked out from: each turned on, allowed and read in full? */
export interface JobData {
    jobs: DataProgress;
    lines: DataProgress;
    /** Job time (job activities): how long items take. */
    times: DataProgress;
}

/** Use on jobs (work orders and completed) over a period: how many jobs, how many of it, and sales and cost before tax. */
export interface Usage {
    jobs: number;
    quantity: number;
    sales: number;
    cost: number;
    /** Of those jobs, how many charged for it with no cost recorded (it had none then): their profit counts in full, so profit may be high. */
    noCostJobs: number;
}

/** Use in one bar of a period's chart: from `from` ("YYYY-MM-DD") to the next bar (shared/periods.ts barStarts). */
export interface BarUsage {
    from: string;
    quantity: number;
    sales: number;
}

export interface ItemStats {
    /** Use in the period asked for (the last 12 months unless another is chosen), and through it as bars. */
    period: Usage;
    bars: BarUsage[];
    /** Quotes in the period: how many it was on, and how many of it. */
    quotes: { quotes: number; quantity: number };
    allTime: Usage;
    /** The last job it was on ("YYYY-MM-DD"), or null. */
    lastUsed: string | null;
    /** Jobs not yet approved that it's on (work orders and completed). With openQuotes, its "active jobs" (activeJobs). */
    openJobs: number;
    /** Quotes it's on that are still quotes (not accepted or unsuccessful yet), of any age. */
    openQuotes: number;
    /** Its active jobs (activeJobs), newest first: the first ACTIVE_JOBS_LISTED. */
    activeList: ActiveJob[];
    /** How long it takes on jobs (stats.ts itemTime); null without job time. */
    time: ItemTime | null;
}

/**
 * How long an item takes on a job (jobTime.ts), in minutes: the first of it, and each extra one on the same job. Null: too few
 * jobs to tell (under the minJobs setting, or that many with more than one).
 */
export interface ItemJobTime {
    uuid: string;
    /** Counted jobs it's on, and how many of them have more than one of it. */
    jobs: number;
    multiJobs: number;
    first: number | null;
    extra: number | null;
}

/** An item's Stats tab: its time (as the Price List settings' preview has it), and how it was worked out. */
export interface ItemTime extends ItemJobTime {
    /** Left out in Price List settings (Leave out items): not timed. */
    leftOut: boolean;
    countDrive: boolean;
    lookBackMonths: number;
    minJobs: number;
    /** The time every job takes before its items (worked out or set), that its times are on top of. */
    everyJob: number | null;
}

/**
 * How long a group's items take (jobTime.ts), in minutes: its timed items' times together, each counted by how often it's used
 * (first: by its jobs; extra: by its jobs with more than one), and the fastest and slowest of their first ones. Null: none timed.
 */
export interface GroupJobTime {
    id: string;
    /** Its items, and those with a time of their own (on enough jobs, not left out). */
    items: number;
    timed: number;
    /** Counted jobs with any of its items on. */
    jobs: number;
    first: number | null;
    extra: number | null;
    fastest: number | null;
    slowest: number | null;
}

/** A group's Stats tab: its time, and how it was worked out. */
export interface GroupTime extends GroupJobTime {
    countDrive: boolean;
    lookBackMonths: number;
    minJobs: number;
    everyJob: number | null;
}

/** What the settings give (Price List settings' preview), to see at a glance whether they're believable. */
export interface JobTimePreview {
    data: JobData;
    /** Jobs counted, and of those found, how many were left out with their customers. */
    jobs: number;
    customerJobs: number;
    /** ...and of the rest, how many were left out for an archived item on them. */
    archivedJobs: number;
    /** The base time per visit: what every visit to a job takes before its items (null: no jobs to tell). */
    everyJob: number | null;
    /** A visit's usual drive there (ServiceM8's estimate), in its time or taken off it (countDrive). */
    drive: number | null;
    /** A counted job's usual time, and how far a job's time usually is from what its items add up to (median, minutes). */
    typicalJob: number | null;
    miss: number | null;
    /** Every item on a counted job (those left out too, without times), most jobs first. */
    items: ItemJobTime[];
    /** Every group with an item on a counted job, most jobs first. */
    groups: GroupJobTime[];
    /** What each rare item adds (one time for all of them). */
    rare: number | null;
    /** Customers with the most counted jobs, for leaving out (names need Clients synced: `clients`). */
    customers: { uuid: string; name: string; jobs: number }[];
    clients: DataProgress;
}

/** Active jobs listed on an item's Stats tab, at most (the count says how many in all). */
export const ACTIVE_JOBS_LISTED = 100;

/** One of an item's active jobs: a quote, or a work order or completed job not yet approved. */
export interface ActiveJob {
    uuid: string;
    /** The job number people know it by ("1042"), or "" if it has none. */
    number: string;
    /** As in ServiceM8: Quote, Work Order or Completed. */
    status: string;
    /** Quoted, completed, made a work order or booked ("YYYY-MM-DD"), or "". */
    day: string;
    /** How many of the item are on it, and their amount before tax, as on the job. */
    quantity: number;
    sales: number;
}

export interface SupplierStats {
    /** Items with a price from them, and for how many they're the cheapest, or what the cost follows. */
    items: number;
    cheapestFor: number;
    costFollows: number;
    /** Their prices due for a recheck, and when one was last checked (ms), or null. */
    due: number;
    lastChecked: number | null;
    /** Their price changes in the period asked for: how many, the average, and the latest increase. */
    changes: { count: number; averagePct: number | null; lastIncrease: { at: number; pct: number; item: string } | null };
    /** Cost of the items used on jobs in the period whose cost comes from them: what's likely bought from them. Null without job data. */
    estimatedSpend: number | null;
}

export interface GroupStats {
    items: number;
    /** Its items' use in the period asked for. */
    period: Usage;
    /** Markup achieved on those jobs (sales over cost, before tax), or null without cost. */
    markupPct: number | null;
    /** Its most used items in the period, by sales. */
    top: { uuid: string; name: string; quantity: number; sales: number }[];
    /** How long its items take on jobs (jobTime.ts); null without job time. */
    time: GroupTime | null;
}

export interface StatsResponse<T> {
    data: JobData;
    stats: T;
}

// ---------------------------------------------------------------------------------------------
// History (history.ts): what changed on items, suppliers and bundles, here or in ServiceM8
// ---------------------------------------------------------------------------------------------

/** The kinds of record with a history (ServiceM8 object names). */
export const TRACKED_OBJECTS = ["material", "supplier", "materialbundle"] as const;
export type TrackedObject = (typeof TRACKED_OBJECTS)[number];

/** One change to one field: its values before and after (as the app reads them), when, and where (here: by whom, if known). */
export interface ChangeRow {
    id: string;
    /** A field, or "created" (made here). */
    field: string;
    before: unknown;
    after: unknown;
    at: number;
    source: "app" | "servicem8";
    by: string | null;
    /** Seen: made here, history only (e.g. stock), or someone pressed Seen. */
    seen: boolean;
}

/** Records with changes from ServiceM8 no one has seen yet, by kind. */
export type ChangedRecords = Record<TrackedObject, string[]>;

/** What an item is found by when searched, here and on the home page: its name, item number, barcode, group's name and tags. */
export const itemSearchText = (m: Pick<MaterialRow, "name" | "item_number" | "barcode">, groupName: string | undefined, tagNames: readonly string[] = []) =>
    `${m.name} ${m.item_number} ${m.barcode} ${groupName ?? ""} ${tagNames.join(" ")}`;

/** Tax rates' short names with these changed (blank: none, its full name again), the rest as they are. The server saves them so, and the screen shows them so while it does. */
export function withShortNames(current: Record<string, string>, changes: Record<string, string>) {
    const next = { ...current, ...changes };
    for (const [uuid, name] of Object.entries(changes)) if (!name) delete next[uuid];
    return next;
}

/**
 * Another rate already read as `name` (its short name, else its full one), which would make the two
 * impossible to tell apart wherever they're picked: that rate's full name, or null.
 */
export function shortNameTaken(rates: readonly { uuid: string; name: string; fullName?: string }[], names: Record<string, string>, uuid: string, name: string): string | null {
    const wanted = name.trim().toLowerCase();
    if (!wanted) return null;
    const other = rates.find((r) => r.uuid !== uuid && (names[r.uuid] || (r.fullName ?? r.name)).trim().toLowerCase() === wanted);
    return other ? (other.fullName ?? other.name) : null;
}

